import type { RouteObject } from 'react-router-dom';
import { crudPermissions } from '@shared/constants/permissions';
import { lazyPage } from '@shared/router/lazy-page';
import type { OrganizationModuleConfig } from './types';

/**
 * One master-data CRUD screen, guarded by its own `<module>.view` key.
 *
 * The config is loaded with the screen (some carry components — Teams brings
 * its employee picker), so a route table stays in the entry bundle without
 * them. `permission` is the config's own permission module, repeated here so
 * the guard is known before anything is downloaded; a mismatch is reported in
 * development.
 */
export function crudRoute(
  path: string,
  permission: string,
  loadConfig: () => Promise<OrganizationModuleConfig>,
): RouteObject {
  return lazyPage(
    path,
    async () => {
      const [{ OrganizationCrudPage }, config] = await Promise.all([
        import('./components/OrganizationCrudPage'),
        loadConfig(),
      ]);
      if (import.meta.env.DEV && config.permission !== permission) {
        console.error(`[routes] ${path}: guard "${permission}" ≠ config "${config.permission}"`);
      }
      return function CrudScreen() {
        return <OrganizationCrudPage config={config} />;
      };
    },
    { permission: crudPermissions(permission).view },
  );
}
